import { Suspense } from 'react'
import { Routes, Route } from 'react-router-dom'
import { Layout } from './components/layout/Layout'
import { ScrollToTop } from './components/common/ScrollToTop'
import { PageLoader } from './components/common/PageLoader'
import { CustomWalletContextProvider } from './contexts/CustomWalletContext'
import { ToastProvider } from './contexts/ToastContext'
import { AskX1ContextProvider } from './components/askx1/AskX1ContextProvider'
import { AskX1Widget } from './components/askx1/AskX1Widget'
import { useEpochMonitor } from './hooks/useEpochMonitor'
import { lazyWithRetry, lazyWithRetryNamed } from './utils/lazyWithRetry'

// Lazy load all page components for optimal code splitting
// Using lazyWithRetry to handle chunk load failures gracefully (auto-reload on stale chunks)
// Core Pages - Most frequently accessed
const Dashboard = lazyWithRetryNamed(() => import('./pages/Dashboard'), 'Dashboard')
const Blocks = lazyWithRetryNamed(() => import('./pages/Blocks'), 'Blocks')
const Transactions = lazyWithRetryNamed(() => import('./pages/Transactions'), 'Transactions')
const Accounts = lazyWithRetryNamed(() => import('./pages/Accounts'), 'Accounts')

// Assets
const Tokens = lazyWithRetryNamed(() => import('./pages/Tokens'), 'Tokens')
const TokenDetails = lazyWithRetryNamed(() => import('./pages/TokenDetails'), 'TokenDetails')
const NFTs = lazyWithRetryNamed(() => import('./pages/NFTs'), 'NFTs')
const NFTDetails = lazyWithRetry(() => import('./pages/NFTDetails'), 'NFTDetails')
const NFTCollection = lazyWithRetry(() => import('./pages/NFTCollection'), 'NFTCollection')

// Validators
const Validators = lazyWithRetryNamed(() => import('./pages/Validators'), 'Validators')
const ValidatorMap = lazyWithRetry(() => import('./pages/ValidatorMap'), 'ValidatorMap')
const ValidatorDetails = lazyWithRetryNamed(() => import('./pages/ValidatorDetails'), 'ValidatorDetails')
const ValidatorStaking = lazyWithRetryNamed(() => import('./pages/ValidatorStaking'), 'ValidatorStaking')
const ValidatorDebug = lazyWithRetry(() => import('./components/ValidatorDebug'), 'ValidatorDebug')

// Programs & Staking
const Programs = lazyWithRetryNamed(() => import('./pages/Programs'), 'Programs')
const ProgramDetails = lazyWithRetry(() => import('./pages/ProgramDetails'), 'ProgramDetails')
const Staking = lazyWithRetryNamed(() => import('./pages/Staking'), 'Staking')
const ProgramStake = lazyWithRetryNamed(() => import('./pages/ProgramStake'), 'ProgramStake')

// DeFi
const DeFiAnalytics = lazyWithRetryNamed(() => import('./pages/DeFiAnalytics'), 'DeFiAnalytics')
const XDEX = lazyWithRetryNamed(() => import('./pages/XDEX'), 'XDEX')
const XDEXDebug = lazyWithRetryNamed(() => import('./pages/XDEXDebug'), 'XDEXDebug')
const VeroAnalytics = lazyWithRetry(() => import('./pages/VeroAnalytics'), 'VeroAnalytics')

// Analytics & Network
const Analytics = lazyWithRetryNamed(() => import('./pages/Analytics'), 'Analytics')
const NetworkStats = lazyWithRetryNamed(() => import('./pages/NetworkStats'), 'NetworkStats')
const GasTracker = lazyWithRetryNamed(() => import('./pages/GasTracker'), 'GasTracker')

// Analytics Sub-pages
const BlockProduction = lazyWithRetry(() => import('./pages/analytics/BlockProduction'), 'BlockProduction')
const BlockExecutiveSummary = lazyWithRetry(() => import('./pages/analytics/BlockExecutiveSummary'), 'BlockExecutiveSummary')
const NetworkConsensus = lazyWithRetry(() => import('./pages/analytics/NetworkConsensus'), 'NetworkConsensus')
const TransactionActivity = lazyWithRetry(() => import('./pages/analytics/TransactionActivity'), 'TransactionActivity')
const NodeConnectivity = lazyWithRetry(() => import('./pages/analytics/NodeConnectivity'), 'NodeConnectivity')
const RpcHealth = lazyWithRetry(() => import('./pages/analytics/RpcHealth'), 'RpcHealth')
const AddressStatistics = lazyWithRetry(() => import('./pages/analytics/AddressStatistics'), 'AddressStatistics')
const FeeTracker = lazyWithRetry(() => import('./pages/analytics/FeeTrackerRpc'), 'FeeTracker')
const RpcCacheMonitor = lazyWithRetry(() => import('./pages/analytics/RpcCacheMonitor'), 'RpcCacheMonitor')
const XntToken = lazyWithRetry(() => import('./pages/analytics/XntToken'), 'XntToken')
const BridgeAnalytics = lazyWithRetry(() => import('./pages/analytics/BridgeAnalytics'), 'BridgeAnalytics')
// Hidden (unlinked) — direct URL only: /analytics/farmer-intel
const FarmerIntel = lazyWithRetry(() => import('./pages/analytics/FarmerIntel'), 'FarmerIntel')
const FarmerIntelCluster = lazyWithRetry(() => import('./pages/analytics/FarmerIntelCluster'), 'FarmerIntelCluster')
const FarmerIntelEvidence = lazyWithRetry(() => import('./pages/analytics/FarmerIntelEvidence'), 'FarmerIntelEvidence')
const FarmerIntelMethodology = lazyWithRetry(() => import('./pages/analytics/FarmerIntelMethodology'), 'FarmerIntelMethodology')

// Detail Pages - TransactionDetails not lazy loaded to prevent white flash
import { TransactionDetails } from './pages/TransactionDetails'
const BlockDetails = lazyWithRetryNamed(() => import('./pages/BlockDetails'), 'BlockDetails')
const AccountDetails = lazyWithRetryNamed(() => import('./pages/AccountDetails'), 'AccountDetails')

// Utility Pages
const APIDocumentation = lazyWithRetryNamed(() => import('./pages/APIDocumentation'), 'APIDocumentation')
const AdvancedSearchPage = lazyWithRetryNamed(() => import('./pages/AdvancedSearchPage'), 'AdvancedSearchPage')
const WalletDashboard = lazyWithRetryNamed(() => import('./pages/WalletDashboard'), 'WalletDashboard')

// Portfolio Suite — full-width multi-page shell + sub-pages
const PortfolioLayout = lazyWithRetry(() => import('./components/portfolio/suite/PortfolioLayout'), 'PortfolioLayout')
const PortfolioDashboard = lazyWithRetry(() => import('./pages/portfolio/PortfolioDashboard'), 'PortfolioDashboard')
const PortfolioAnalytics = lazyWithRetry(() => import('./pages/portfolio/PortfolioAnalytics'), 'PortfolioAnalytics')
const PortfolioAssets = lazyWithRetry(() => import('./pages/portfolio/PortfolioAssets'), 'PortfolioAssets')
const PortfolioDeFi = lazyWithRetry(() => import('./pages/portfolio/PortfolioDeFi'), 'PortfolioDeFi')
const PortfolioTransactions = lazyWithRetry(() => import('./pages/portfolio/PortfolioTransactions'), 'PortfolioTransactions')
const PortfolioActivity = lazyWithRetry(() => import('./pages/portfolio/PortfolioActivity'), 'PortfolioActivity')
const PortfolioNFTs = lazyWithRetry(() => import('./pages/portfolio/PortfolioNFTs'), 'PortfolioNFTs')
const PortfolioPositions = lazyWithRetry(() => import('./pages/portfolio/PortfolioPositions'), 'PortfolioPositions')
const PortfolioAggregate = lazyWithRetry(() => import('./pages/portfolio/PortfolioAggregate'), 'PortfolioAggregate')
const DatabaseDebug = lazyWithRetry(() => import('./pages/DatabaseDebug'), 'DatabaseDebug')
const Disclaimer = lazyWithRetryNamed(() => import('./pages/Disclaimer'), 'Disclaimer')
const BrandGuidelines = lazyWithRetryNamed(() => import('./pages/BrandGuidelines'), 'BrandGuidelines')
const FullScreenTable = lazyWithRetry(() => import('./pages/FullScreenTable'), 'FullScreenTable')
const NotFound = lazyWithRetryNamed(() => import('./pages/NotFound'), 'NotFound')

function App() {
  // Initialize epoch monitoring at app root level
  useEpochMonitor()

  return (
    <CustomWalletContextProvider>
      <ToastProvider>
        <AskX1ContextProvider>
        <ScrollToTop />
        <Layout>
          <Suspense fallback={<PageLoader />}>
            <Routes>
              {/* Main Pages */}
              <Route path="/" element={<Dashboard />} />
              <Route path="/blocks" element={<Blocks />} />
              <Route path="/transactions" element={<Transactions />} />
              <Route path="/accounts" element={<Accounts />} />
              <Route path="/tokens" element={<Tokens />} />
              <Route path="/validators" element={<Validators />} />
              <Route path="/validator-map" element={<ValidatorMap />} />
              <Route path="/validators/:validatorAddress" element={<ValidatorDetails />} />
              <Route path="/stake/:validatorAddress" element={<ValidatorStaking />} />
              <Route path="/validator-debug" element={<ValidatorDebug />} />
              <Route path="/analytics" element={<Analytics />} />
              <Route path="/programs" element={<Programs />} />
              <Route path="/nfts" element={<NFTs />} />
              <Route path="/defi" element={<DeFiAnalytics />} />
              <Route path="/defi/xdex" element={<XDEX />} />
              <Route path="/defi/xdex/debug" element={<XDEXDebug />} />
              <Route path="/defi/vero" element={<VeroAnalytics />} />
              <Route path="/staking" element={<Staking />} />
              <Route path="/delegations" element={<ProgramStake />} />
              <Route path="/network-stats" element={<NetworkStats />} />
              <Route path="/gas-tracker" element={<GasTracker />} />
              <Route path="/api-docs" element={<APIDocumentation />} />
              <Route path="/search" element={<AdvancedSearchPage />} />
              {/* Portfolio Suite — full-width multi-page shell wraps the sub-pages */}
              <Route path="/portfolio" element={<PortfolioLayout />}>
                <Route index element={<PortfolioDashboard />} />
                <Route path="analytics" element={<PortfolioAnalytics />} />
                <Route path="assets" element={<PortfolioAssets />} />
                <Route path="defi" element={<PortfolioDeFi />} />
                <Route path="transactions" element={<PortfolioTransactions />} />
                <Route path="activity" element={<PortfolioActivity />} />
                <Route path="nfts" element={<PortfolioNFTs />} />
                <Route path="positions" element={<PortfolioPositions />} />
                <Route path="aggregate" element={<PortfolioAggregate />} />
              </Route>
              <Route path="/wallet" element={<WalletDashboard />} /> {/* Legacy single-page dashboard */}
              <Route path="/disclaimer" element={<Disclaimer />} />
              <Route path="/brand" element={<BrandGuidelines />} />
              <Route path="/debug" element={<DatabaseDebug />} />

              {/* Analytics Pages */}
              <Route path="/analytics/block-production" element={<BlockProduction />} />
              <Route path="/analytics/block-executive" element={<BlockExecutiveSummary />} />
              <Route path="/analytics/network-consensus" element={<NetworkConsensus />} />
              <Route path="/analytics/transaction-activity" element={<TransactionActivity />} />
              <Route path="/analytics/node-connectivity" element={<NodeConnectivity />} />
              <Route path="/analytics/rpc-health" element={<RpcHealth />} />
              <Route path="/analytics/address-statistics" element={<AddressStatistics />} />
              <Route path="/analytics/fee-tracker" element={<FeeTracker />} />
              <Route path="/analytics/rpc-cache-monitor" element={<RpcCacheMonitor />} />
              <Route path="/analytics/xnt" element={<XntToken />} />
              <Route path="/analytics/bridge" element={<BridgeAnalytics />} />
              {/* Hidden — not in nav; direct URL only */}
              <Route path="/analytics/farmer-intel" element={<FarmerIntel />} />
              <Route path="/analytics/farmer-intel/evidence" element={<FarmerIntelEvidence />} />
              <Route path="/analytics/farmer-intel/methodology" element={<FarmerIntelMethodology />} />
              <Route path="/analytics/farmer-intel/cluster/:authority" element={<FarmerIntelCluster />} />
              <Route path="/fullscreen-table" element={<FullScreenTable />} />

              {/* Detail Pages */}
              <Route path="/block/:blockNumber" element={<BlockDetails />} />
              <Route path="/tx/:txHash" element={<TransactionDetails />} />
              <Route path="/account/:address" element={<AccountDetails />} />
              <Route path="/accounts/:address" element={<AccountDetails />} />
              <Route path="/address/:address" element={<AccountDetails />} />
              <Route path="/token/:tokenAddress" element={<TokenDetails />} />
              <Route path="/tokens/:tokenAddress" element={<TokenDetails />} />
              <Route path="/program/:programId" element={<ProgramDetails />} />
              <Route path="/nft/:mintAddress" element={<NFTDetails />} />
              <Route path="/nft-collection/:collectionId" element={<NFTCollection />} />

              {/* 404 Page */}
              <Route path="*" element={<NotFound />} />
            </Routes>
          </Suspense>
          <AskX1Widget />
        </Layout>
        </AskX1ContextProvider>
      </ToastProvider>
    </CustomWalletContextProvider>
  )
}

export default App
